import React, { useEffect, useState } from 'react';
import Icon from '../components/ui/Icon';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';

import DiagnosticHeader from '../components/diagnostic/DiagnosticHeader';
import SummaryTriad from '../components/diagnostic/SummaryTriad';
import KnowledgePanel from '../components/diagnostic/KnowledgePanel';
import BehaviourInsights from '../components/diagnostic/BehaviourInsights';
import TopicMasteryList from '../components/diagnostic/TopicMasteryList';
import StrengthsImprovements from '../components/diagnostic/StrengthsImprovements';
import RecommendationList from '../components/diagnostic/RecommendationList';

import '../components/diagnostic/DiagnosticComponents.css';
import '../components/diagnostic/DiagnosticReportPro.css';

const Section = ({ title, description, children }) => (
  <section className="dxr-section">
    {title && (
      <div className="dxr-section-head">
        <h2 className="dxr-section-title">{title}</h2>
        {description && <p className="dxr-section-desc">{description}</p>}
      </div>
    )}
    {children}
  </section>
);

const DiagnosticReport = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, [attemptId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/diagnostic/report/${attemptId}`);
      setReport(res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load diagnostic report.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="diagnostic-page-bg dxr-center">
        <div className="dxr-loading">Compiling assessment report…</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="diagnostic-page-bg" style={{ padding: '40px' }}>
        <button onClick={() => navigate(-1)} className="dxr-back-btn" style={{ marginBottom: 20 }}><Icon name="chevron-left" size={16} /> Back</button>
        <div className="dxr-empty-state">
          <div className="dxr-empty-icon"><Icon name="warning" size={36} /></div>
          <h3>Report Unavailable</h3>
          <p>{error || 'This report is either pending calculation or does not exist.'}</p>
        </div>
      </div>
    );
  }

  const { student, overall, knowledge, topics, metrics, strengths, EIP } = report;

  // Behaviour score: prefer the additive backend field; otherwise average the four metrics.
  const behaviourScore = (report.behaviour_score != null)
    ? report.behaviour_score
    : Math.round(
        ['TPSI', 'CER', 'RCI', 'FFI']
          .map(k => Number(metrics?.[k]?.score) || 0)
          .reduce((a, b) => a + b, 0) / 4
      );

  const knowledgeScore = knowledge?.score != null ? knowledge.score : null;
  const eriScore = overall?.score != null ? Number(overall.score) : null;

  const dateObj = new Date(student.diagnostic_date);
  const formattedDate = !isNaN(dateObj)
    ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Unknown Date';

  return (
    <div className="diagnostic-page-bg">
      <div className="diagnostic-dashboard-wrapper">

        <DiagnosticHeader student={student} formattedDate={formattedDate} />

        {/* SECTION 1 — Overall Assessment Summary */}
        <Section>
          <SummaryTriad
            knowledgeScore={knowledgeScore}
            behaviourScore={behaviourScore}
            eriScore={eriScore}
          />
        </Section>

        {/* SECTION 2 — Knowledge Performance */}
        {knowledge && (
          <Section title="Knowledge Performance" description="What the student demonstrated academically.">
            <KnowledgePanel knowledge={knowledge} />
          </Section>
        )}

        {/* SECTION 3 — Behaviour Insights */}
        {metrics && (
          <Section title="Behaviour Insights" description="How the student worked through the assessment.">
            <BehaviourInsights metrics={metrics} />
          </Section>
        )}

        {/* SECTION 4 — Topic Mastery */}
        {topics && topics.length > 0 && (
          <Section title="Topic Mastery" description="Strongest to weakest, by accuracy.">
            <TopicMasteryList topics={topics} />
          </Section>
        )}

        {/* SECTIONS 5 & 6 — Strengths & Improvement Areas */}
        <Section title="Strengths & Improvement Areas">
          <StrengthsImprovements strengths={strengths} metrics={metrics} topics={topics} />
        </Section>

        {/* SECTION 7 — Personalized Learning Recommendations */}
        <Section title="Personalized Learning Recommendations">
          <RecommendationList recommendations={EIP} />
        </Section>

        {/* SECTION 8 — Performance Timeline: hidden gracefully (no attempt history available). */}

      </div>
    </div>
  );
};

export default DiagnosticReport;
